import "chart.js/auto";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Bar, Line, Pie } from "react-chartjs-2";
// eslint-disable-next-line no-unused-vars
import { baseUrl } from "../config"; // kept for when real data is wired in
import SEO from '../components/SEO';
import headerBg from '../assets/img/Productionstatus.jpg';

const productionStages = [
  "Progress Tracker",
  "Calendar",
  "Editing",
  "Scheduling",
];

const graphTypes = [
  { label: "Bar Chart", value: "bar" },
  { label: "Line Chart", value: "line" },
  { label: "Pie Chart", value: "pie" },
];

// ⚠️ SAMPLE DATA — this does not call the backend yet (random values per stage).
const fetchData = async (stage) => {
  const response = await new Promise((resolve) =>
    setTimeout(() => {
      resolve({
        labels: ["Task A", "Task B", "Task C", "Task D"],
        data: [Math.random() * 20, Math.random() * 30, Math.random() * 15, Math.random() * 25],
      });
    }, 1000)
  );
  return response;
};

// Dark-theme chart palette (no purple)
const CHART_COLORS = ["#fb7185", "#60a5fa", "#fcd34d", "#2dd4bf", "#fdba74", "#86efac"];
const TEAL = "#2dd4bf";

// ==================== ICONS (inline SVG) ====================
const PATHS = {
  reel: (<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="6.5" r="1.5" /><circle cx="12" cy="17.5" r="1.5" /><circle cx="6.5" cy="12" r="1.5" /><circle cx="17.5" cy="12" r="1.5" /></>),
  barChart: (<><line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" /></>),
  download: (<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>),
  info: (<><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>),
};

const Ico = ({ name, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
    {PATHS[name]}
  </svg>
);

const ProductionStatus = () => {
  const [selectedStage, setSelectedStage] = useState(productionStages[0]);
  const [selectedGraphType, setSelectedGraphType] = useState(graphTypes[0].value);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const chartBoxRef = useRef(null);

  useEffect(() => {
    const loadChartData = async () => {
      setLoading(true);
      const data = await fetchData(selectedStage);
      setChartData({
        labels: data.labels,
        datasets: [
          {
            label: selectedStage,
            data: data.data,
            backgroundColor: [
              "rgba(255, 99, 132, 0.6)",
              "rgba(54, 162, 235, 0.6)",
              "rgba(255, 206, 86, 0.6)",
              "rgba(75, 192, 192, 0.6)",
            ],
          },
        ],
      });
      setLoading(false);
    };

    loadChartData();
  }, [selectedStage]);

  // UI-only: restyle the same data for the dark theme, per chart type
  const styledData = useMemo(() => {
    if (!chartData) return null;
    const colors = chartData.labels.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]);
    return {
      labels: chartData.labels,
      datasets: chartData.datasets.map(ds => {
        if (selectedGraphType === "line") {
          return {
            ...ds,
            borderColor: TEAL,
            backgroundColor: "rgba(45, 212, 191, 0.15)",
            pointBackgroundColor: TEAL,
            pointBorderColor: "#1b2433",
            pointRadius: 5,
            pointHoverRadius: 7,
            borderWidth: 3,
            tension: 0.35,
            fill: true,
          };
        }
        if (selectedGraphType === "pie") {
          return { ...ds, backgroundColor: colors, borderColor: "#1b2433", borderWidth: 3, hoverOffset: 8 };
        }
        return { ...ds, backgroundColor: colors, borderRadius: 6, maxBarThickness: 90 };
      }),
    };
  }, [chartData, selectedGraphType]);

  const chartOptions = useMemo(() => {
    const isPie = selectedGraphType === "pie";
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: isPie,
          position: "bottom",
          labels: { color: "#cbd5e1", boxWidth: 12, boxHeight: 12, padding: 18 },
        },
        tooltip: {
          backgroundColor: "#0b1420",
          borderColor: "rgba(255,255,255,0.12)",
          borderWidth: 1,
          titleColor: "#ffffff",
          bodyColor: "#cbd5e1",
          padding: 10,
          callbacks: {
            label: (ctx) => {
              const v = typeof ctx.parsed === "object" ? ctx.parsed.y : ctx.parsed;
              return ` ${ctx.label}: ${Number(v).toFixed(1)}`;
            },
          },
        },
      },
    };
    if (!isPie) {
      options.scales = {
        x: { ticks: { color: "#94a3b8" }, grid: { color: "rgba(255,255,255,0.05)" } },
        y: { beginAtZero: true, ticks: { color: "#94a3b8" }, grid: { color: "rgba(255,255,255,0.07)" } },
      };
    }
    return options;
  }, [selectedGraphType]);

  // Generate Report: downloads the current chart as a PNG with a title header (client-side, no API)
  const handleGenerateReport = () => {
    const source = chartBoxRef.current && chartBoxRef.current.querySelector("canvas");
    if (!source) return;

    const scale = window.devicePixelRatio || 1;
    const pad = 32 * scale;
    const headerH = 96 * scale;
    const out = document.createElement("canvas");
    out.width = source.width + pad * 2;
    out.height = source.height + headerH + pad;
    const ctx = out.getContext("2d");

    ctx.fillStyle = "#111827";
    ctx.fillRect(0, 0, out.width, out.height);

    const graphLabel = (graphTypes.find(g => g.value === selectedGraphType) || {}).label || "";
    const dateLabel = new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });

    ctx.fillStyle = "#ffffff";
    ctx.font = `700 ${22 * scale}px sans-serif`;
    ctx.fillText("Production Status Report", pad, pad + 20 * scale);
    ctx.fillStyle = "#94a3b8";
    ctx.font = `400 ${14 * scale}px sans-serif`;
    ctx.fillText(`${selectedStage} · ${graphLabel} · ${dateLabel} · Sample data`, pad, pad + 46 * scale);

    ctx.drawImage(source, pad, headerH);

    const link = document.createElement("a");
    const safeStage = selectedStage.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    link.download = `production-status-${safeStage}-${new Date().toISOString().slice(0, 10)}.png`;
    link.href = out.toDataURL("image/png");
    link.click();
  };

  return (
    <div className="ps2-root">
      <style>{PS2_STYLES}</style>
      <SEO
        title="Production Status"
        description="Track the current status and milestones of your film production on ProducerBuddy."
        url="https://producerbuddy.pilomation.com/production-status"
        robots="noindex, nofollow"
      />

      {/* Header banner */}
      <header className="ps2-hero">
        <div className="ps2-hero-bg" style={{ backgroundImage: `url(${headerBg})` }} />
        <div className="ps2-hero-overlay" />
        <div className="ps2-hero-content">
          <h1 className="ps2-hero-title">Production Status Dashboard</h1>
          <p className="ps2-hero-sub">Stay on track, from concept to completion.</p>
        </div>
      </header>

      <div className="ps2-body">
        {/* Controls */}
        <div className="ps2-controls">
          <div className="ps2-control">
            <span className="ps2-control-tile"><Ico name="reel" size={20} /></span>
            <div className="ps2-control-field">
              <label htmlFor="ps2-stage">Select production stage</label>
              <select
                id="ps2-stage"
                className="ps2-select"
                value={selectedStage}
                onChange={(e) => setSelectedStage(e.target.value)}
              >
                {productionStages.map((stage) => (
                  <option key={stage} value={stage}>
                    {stage}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="ps2-control">
            <span className="ps2-control-tile"><Ico name="barChart" size={20} /></span>
            <div className="ps2-control-field">
              <label htmlFor="ps2-graph">Select graph type</label>
              <select
                id="ps2-graph"
                className="ps2-select"
                value={selectedGraphType}
                onChange={(e) => setSelectedGraphType(e.target.value)}
              >
                {graphTypes.map((graph) => (
                  <option key={graph.value} value={graph.value}>
                    {graph.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            type="button"
            className="ps2-btn"
            onClick={handleGenerateReport}
            disabled={loading || !chartData}
          >
            <Ico name="download" size={16} /> Generate Report
          </button>
        </div>

        {/* Chart card */}
        <section className="ps2-card">
          <div className="ps2-card-head">
            <span className="ps2-card-icon"><Ico name="barChart" size={22} /></span>
            <div className="ps2-card-text">
              <h2 className="ps2-card-title">Production progress overview</h2>
              <p className="ps2-card-sub">Track tasks across different stages of production.</p>
            </div>
            <div className="ps2-card-tags">
              <span className="ps2-chip">{selectedStage}</span>
              <span className="ps2-chip ps2-chip--sample" title="These numbers are placeholders until live project data is connected.">
                <Ico name="info" size={13} /> Sample data
              </span>
            </div>
          </div>

          <div className="ps2-chart" ref={chartBoxRef}>
            {loading ? (
              <div className="ps2-chart-loading">
                <div className="ps2-skeleton" />
                <p>Loading chart data...</p>
              </div>
            ) : styledData ? (
              <>
                {selectedGraphType === "bar" && <Bar data={styledData} options={chartOptions} />}
                {selectedGraphType === "line" && <Line data={styledData} options={chartOptions} />}
                {selectedGraphType === "pie" && <Pie data={styledData} options={chartOptions} />}
              </>
            ) : (
              <p className="ps2-empty">No data available</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

// ==================== STYLES (scoped ps2- classes, kept in this file) ====================
const PS2_STYLES = `
.ps2-root {
  --ps2-teal: #0fb3ad; --ps2-teal-hover: #0c9a95; --ps2-teal-light: #5eead4;
  --ps2-teal-soft: rgba(15,179,173,0.12); --ps2-teal-border: rgba(15,179,173,0.45);
  --ps2-bg: #111827; --ps2-surface: #1b2433; --ps2-border: rgba(255,255,255,0.08);
  --ps2-text: #f3f5f7; --ps2-muted: #94a3b8;
  background: var(--ps2-bg); color: var(--ps2-text); min-height: calc(100vh - 130px);
}
.ps2-root button { font-family: inherit; }
.ps2-root button:focus-visible, .ps2-root select:focus-visible { outline: 2px solid var(--ps2-teal-light); outline-offset: 2px; }

/* Hero */
.ps2-hero { position: relative; height: 230px; overflow: hidden; }
.ps2-hero-bg { position: absolute; inset: 0; background-size: cover; background-position: center 45%; filter: brightness(1.08) saturate(1.15); }
.ps2-hero-overlay { position: absolute; inset: 0; background:
  linear-gradient(90deg, rgba(8,15,25,0.92) 0%, rgba(8,15,25,0.62) 40%, rgba(8,15,25,0.08) 75%, rgba(8,15,25,0.2) 100%),
  linear-gradient(180deg, rgba(17,24,39,0) 55%, rgba(17,24,39,1) 100%); }
.ps2-hero-content { position: relative; z-index: 1; height: 100%; display: flex; flex-direction: column; justify-content: center; padding: 0 2rem; }
.ps2-hero-title { margin: 0; font-size: 28px; font-weight: 700; line-height: 1.25; color: #fff; }
.ps2-hero-sub { margin: 8px 0 0; font-size: 15px; line-height: 1.5; color: #cbd5e1; }

/* Body */
.ps2-body { padding: 1.25rem 2rem 2.5rem; }

/* Controls */
.ps2-controls { display: flex; align-items: center; gap: 24px; flex-wrap: wrap; padding: 18px 22px;
  background: var(--ps2-surface); border: 1px solid var(--ps2-border); border-radius: 14px; }
.ps2-control { display: flex; align-items: center; gap: 14px; flex: 1 1 260px; min-width: 0; }
.ps2-control-tile { width: 44px; height: 44px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
  border-radius: 10px; background: var(--ps2-teal-soft); color: var(--ps2-teal-light); }
.ps2-control-field { flex: 1; min-width: 0; }
.ps2-control-field label { display: block; margin-bottom: 6px; font-size: 13px; font-weight: 600; color: var(--ps2-text); }
.ps2-select { width: 100%; max-width: 280px; height: 40px; padding: 0 36px 0 12px; appearance: none; -webkit-appearance: none;
  background-color: rgba(255,255,255,0.04);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 12px center;
  border: 1px solid rgba(255,255,255,0.14); border-radius: 10px; color: var(--ps2-text); font-size: 14px; cursor: pointer; outline: none;
  transition: border-color .15s ease; }
.ps2-select:focus { border-color: var(--ps2-teal); }
.ps2-select option { background: #1b2433; color: #f3f5f7; }
.ps2-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; height: 42px; padding: 0 20px; margin-left: auto;
  border: none; border-radius: 10px; background: var(--ps2-teal); color: #fff; font-size: 14px; font-weight: 600; cursor: pointer;
  white-space: nowrap; transition: background-color .15s ease; }
.ps2-btn:hover:not(:disabled) { background: var(--ps2-teal-hover); }
.ps2-btn:disabled { opacity: .55; cursor: not-allowed; }

/* Chart card */
.ps2-card { max-width: 920px; margin: 28px auto 0; padding: 22px 24px 24px; background: var(--ps2-surface);
  border: 1px solid var(--ps2-border); border-radius: 16px; }
.ps2-card-head { display: flex; align-items: flex-start; gap: 14px; flex-wrap: wrap; margin-bottom: 20px; }
.ps2-card-icon { color: var(--ps2-teal-light); padding-top: 2px; }
.ps2-card-text { flex: 1; min-width: 200px; }
.ps2-card-title { margin: 0; font-size: 17px; font-weight: 600; color: var(--ps2-text); }
.ps2-card-sub { margin: 4px 0 0; font-size: 14px; color: var(--ps2-muted); }
.ps2-card-tags { display: flex; gap: 8px; flex-wrap: wrap; }
.ps2-chip { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 999px;
  background: var(--ps2-teal-soft); color: var(--ps2-teal-light); font-size: 12px; font-weight: 500; }
.ps2-chip--sample { background: rgba(245,158,11,0.14); color: #fcd34d; cursor: help; }
.ps2-chart { position: relative; height: 360px; }
.ps2-chart-loading { height: 100%; display: flex; flex-direction: column; gap: 12px; }
.ps2-chart-loading p { margin: 0; text-align: center; font-size: 14px; color: var(--ps2-muted); }
.ps2-skeleton { flex: 1; border-radius: 12px; background: linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%);
  background-size: 200% 100%; animation: ps2-shimmer 1.2s linear infinite; }
@keyframes ps2-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.ps2-empty { margin: 0; height: 100%; display: flex; align-items: center; justify-content: center; color: var(--ps2-muted); font-size: 14px; }

/* Responsive */
@media (max-width: 900px) {
  .ps2-btn { margin-left: 0; width: 100%; }
  .ps2-select { max-width: none; }
}
@media (max-width: 768px) {
  .ps2-hero { height: 190px; }
  .ps2-hero-content { padding: 0 16px; }
  .ps2-hero-title { font-size: 23px; }
  .ps2-body { padding: 16px 16px 32px; }
  .ps2-card { padding: 18px 16px 20px; }
  .ps2-chart { height: 300px; }
}
@media (prefers-reduced-motion: reduce) {
  .ps2-skeleton { animation: none; }
  .ps2-btn, .ps2-select { transition: none; }
}
`;

export default ProductionStatus;